import { getStoredSession } from '@/auth/sessionStorage';
import { buildStorageKeyForNamespace, resolveCacheNamespace } from '@/shared/data/cacheNamespace';
import type {
  BootstrapSnapshot,
  OrganisationOption,
  ProjectOption,
} from './useBootstrapData.types';
import type { AuthSession } from '@/auth/types';
import type { ProjectDto, QueryResult } from '@flowqa/shared';

const ORG_OPTIONS_CACHE_KEY = 'flowqa-org-options-cache';
const PROJECT_OPTIONS_CACHE_KEY = 'flowqa-project-options-cache';
const BOOTSTRAP_SNAPSHOT_CACHE_KEY = 'flowqa-bootstrap-snapshot-cache';
const bootstrapSnapshotTtlMs = 5 * 60_000;

type ResolveBootstrapCacheSeedInput = {
  bootstrapUserId: string | null;
  selectedOrganisationId: string | null;
  session: AuthSession | null;
};

export type BootstrapCacheSeed = {
  allowCachedBootstrap: boolean;
  freshSnapshot: BootstrapSnapshot | null;
  persistedSnapshot: BootstrapSnapshot | null;
  initialCachedProjectOptions: ProjectOption[];
  initialOrganisations: OrganisationOption[];
  initialProjects: ProjectDto[];
  resolvedOrganisationId: string | null;
};

export const buildScopedStorageKey = (baseKey: string, userId?: string | null): string =>
  buildStorageKeyForNamespace(baseKey, resolveCacheNamespace(userId));

export const hasServerOrganisationContext = (session: AuthSession | null): boolean => {
  if (!session) return false;
  const serverOrganisationId = session.lastOrganisationId ?? session.orgId ?? null;
  return typeof serverOrganisationId === 'string' && serverOrganisationId.trim().length > 0;
};

export const writeCachedOrganisations = (
  organisations: OrganisationOption[],
  userId?: string | null,
): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    buildScopedStorageKey(ORG_OPTIONS_CACHE_KEY, userId),
    JSON.stringify(organisations),
  );
};

export const clearCachedOrganisations = (userId?: string | null): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(buildScopedStorageKey(ORG_OPTIONS_CACHE_KEY, userId));
};

export const readCachedOrganisations = (userId?: string | null): OrganisationOption[] => {
  if (typeof window === 'undefined') return [];
  const raw = window.localStorage.getItem(buildScopedStorageKey(ORG_OPTIONS_CACHE_KEY, userId));
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is OrganisationOption =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as { id?: unknown }).id === 'string' &&
        typeof (item as { name?: unknown }).name === 'string' &&
        typeof (item as { slug?: unknown }).slug === 'string' &&
        ((item as { clerkOrganizationId?: unknown }).clerkOrganizationId === null ||
          typeof (item as { clerkOrganizationId?: unknown }).clerkOrganizationId === 'string'),
    );
  } catch {
    return [];
  }
};

export const toProjectOption = (project: ProjectDto): ProjectOption => ({
  id: project.id,
  name: project.name,
  slug: project.slug,
  organisationId: project.organisationId,
  badgeChars: project.badgeChars,
  badgeColor: project.badgeColor,
  badgeEnabled: project.badgeEnabled,
});

export const upsertProjectById = (items: ProjectDto[], nextItem: ProjectDto): ProjectDto[] => {
  const existingIndex = items.findIndex((item) => item.id === nextItem.id);
  if (existingIndex === -1) {
    return [...items, nextItem];
  }
  const next = [...items];
  next[existingIndex] = nextItem;
  return next;
};

export const upsertProjectOptionById = (
  items: ProjectOption[],
  nextItem: ProjectOption,
): ProjectOption[] => {
  const existingIndex = items.findIndex((item) => item.id === nextItem.id);
  if (existingIndex === -1) {
    return [...items, nextItem];
  }
  const next = [...items];
  next[existingIndex] = nextItem;
  return next;
};

export const mergeProjectsById = (items: ProjectDto[]): ProjectDto[] => {
  const merged = new Map<string, ProjectDto>();
  items.forEach((item) => {
    merged.set(item.id, item);
  });
  return [...merged.values()];
};

const filterProjectsByOrganisationId = (
  projects: ProjectDto[],
  organisationId: string | null,
): ProjectDto[] => projects.filter((project) => project.organisationId === organisationId);

export const upsertProjectQueryResult = (
  result: QueryResult<ProjectDto> | undefined,
  nextProject: ProjectDto,
): QueryResult<ProjectDto> => {
  const nextItems = upsertProjectById(result?.items ?? [], nextProject);
  return {
    items: nextItems,
    total: nextItems.length,
    page: result?.page ?? 1,
    pageSize: result?.pageSize ?? Math.max(nextItems.length, 1),
  };
};

const readProjectOptionsCacheMap = (userId?: string | null): Record<string, ProjectOption[]> => {
  if (typeof window === 'undefined') return {};
  const raw = window.localStorage.getItem(buildScopedStorageKey(PROJECT_OPTIONS_CACHE_KEY, userId));
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return {};
    const entries = Object.entries(parsed);
    const validEntries = entries
      .map(([key, value]): [string, ProjectOption[]] | null => {
        if (!Array.isArray(value)) return null;
        const projects = value.flatMap((item): ProjectOption[] => {
          if (typeof item !== 'object' || item === null) return [];
          const badgeEnabledRaw = (item as { badgeEnabled?: unknown }).badgeEnabled;
          const badgeEnabled = typeof badgeEnabledRaw === 'boolean' ? badgeEnabledRaw : true;
          const organisationId = (item as { organisationId?: unknown }).organisationId;
          if (typeof (item as { id?: unknown }).id !== 'string') return [];
          if (typeof (item as { name?: unknown }).name !== 'string') return [];
          if (typeof (item as { slug?: unknown }).slug !== 'string') return [];
          if (typeof (item as { badgeChars?: unknown }).badgeChars !== 'string') return [];
          if (typeof (item as { badgeColor?: unknown }).badgeColor !== 'string') return [];
          if (organisationId !== null && typeof organisationId !== 'string') return [];
          return [
            {
              id: (item as { id: string }).id,
              name: (item as { name: string }).name,
              slug: (item as { slug: string }).slug,
              organisationId: organisationId as string | null,
              badgeChars: (item as { badgeChars: string }).badgeChars,
              badgeColor: (item as { badgeColor: ProjectDto['badgeColor'] }).badgeColor,
              badgeEnabled,
            },
          ];
        });
        return [key, projects];
      })
      .filter((entry): entry is [string, ProjectOption[]] => entry !== null);
    return Object.fromEntries(validEntries);
  } catch {
    return {};
  }
};

const getProjectCacheKey = (organisationId: string | null): string => organisationId ?? 'personal';

const writeProjectOptionsCacheMap = (
  map: Record<string, ProjectOption[]>,
  userId?: string | null,
): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    buildScopedStorageKey(PROJECT_OPTIONS_CACHE_KEY, userId),
    JSON.stringify(map),
  );
};

export const writeCachedProjectOptions = (
  organisationId: string | null,
  projects: ProjectOption[],
  userId?: string | null,
): void => {
  if (typeof window === 'undefined') return;
  const map = readProjectOptionsCacheMap(userId);
  map[getProjectCacheKey(organisationId)] = projects;
  window.localStorage.setItem(
    buildScopedStorageKey(PROJECT_OPTIONS_CACHE_KEY, userId),
    JSON.stringify(map),
  );
};

export const clearCachedProjectOptions = (userId?: string | null): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(buildScopedStorageKey(PROJECT_OPTIONS_CACHE_KEY, userId));
};

export const readCachedProjectOptions = (
  organisationId: string | null,
  userId?: string | null,
): ProjectOption[] => {
  const map = readProjectOptionsCacheMap(userId);
  return map[getProjectCacheKey(organisationId)] ?? [];
};

export const resolveBootstrapCacheSeed = ({
  bootstrapUserId,
  selectedOrganisationId,
  session,
}: ResolveBootstrapCacheSeedInput): BootstrapCacheSeed => {
  const allowCachedBootstrap = bootstrapUserId !== null && hasServerOrganisationContext(session);
  const freshSnapshot = allowCachedBootstrap ? readFreshBootstrapSnapshot(bootstrapUserId) : null;
  const persistedSnapshot = allowCachedBootstrap ? readBootstrapSnapshot(bootstrapUserId) : null;
  const resolvedOrganisationId = allowCachedBootstrap
    ? (selectedOrganisationId ?? session?.lastOrganisationId ?? session?.orgId ?? null)
    : null;
  const scopedFreshProjects = freshSnapshot
    ? filterProjectsByOrganisationId(freshSnapshot.projects, resolvedOrganisationId)
    : [];

  return {
    allowCachedBootstrap,
    freshSnapshot,
    persistedSnapshot,
    initialProjects: scopedFreshProjects,
    initialOrganisations: freshSnapshot?.organisations ?? readCachedOrganisations(bootstrapUserId),
    initialCachedProjectOptions: freshSnapshot
      ? scopedFreshProjects.map(toProjectOption)
      : readCachedProjectOptions(resolvedOrganisationId, bootstrapUserId),
    resolvedOrganisationId,
  };
};

const isOrganisationOption = (value: unknown): value is OrganisationOption =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as { id?: unknown }).id === 'string' &&
  typeof (value as { name?: unknown }).name === 'string' &&
  typeof (value as { slug?: unknown }).slug === 'string';

const isProjectDto = (value: unknown): value is ProjectDto =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as { id?: unknown }).id === 'string' &&
  typeof (value as { name?: unknown }).name === 'string' &&
  typeof (value as { slug?: unknown }).slug === 'string' &&
  typeof (value as { badgeChars?: unknown }).badgeChars === 'string' &&
  typeof (value as { badgeColor?: unknown }).badgeColor === 'string' &&
  typeof (value as { badgeEnabled?: unknown }).badgeEnabled === 'boolean' &&
  ((value as { organisationId?: unknown }).organisationId === null ||
    typeof (value as { organisationId?: unknown }).organisationId === 'string');

export const writeBootstrapSnapshot = (
  snapshot: BootstrapSnapshot,
  userId?: string | null,
): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    buildScopedStorageKey(BOOTSTRAP_SNAPSHOT_CACHE_KEY, userId),
    JSON.stringify(snapshot),
  );
};

export const clearBootstrapSnapshot = (userId?: string | null): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(buildScopedStorageKey(BOOTSTRAP_SNAPSHOT_CACHE_KEY, userId));
};

export const clearProjectStateCaches = (userId?: string | null): void => {
  clearCachedOrganisations(userId);
  clearCachedProjectOptions(userId);
  clearBootstrapSnapshot(userId);
};

export const readBootstrapSnapshot = (userId?: string | null): BootstrapSnapshot | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(
    buildScopedStorageKey(BOOTSTRAP_SNAPSHOT_CACHE_KEY, userId),
  );
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const savedAt = (parsed as { savedAt?: unknown }).savedAt;
    const organisations = (parsed as { organisations?: unknown }).organisations;
    const projects = (parsed as { projects?: unknown }).projects;
    if (typeof savedAt !== 'number') return null;
    if (!Array.isArray(organisations) || !Array.isArray(projects)) return null;
    const validOrganisations = organisations.filter(isOrganisationOption);
    const validProjects = projects.filter(isProjectDto);
    if (validOrganisations.length !== organisations.length) return null;
    if (validProjects.length !== projects.length) return null;
    return {
      savedAt,
      organisations: validOrganisations,
      projects: validProjects,
    };
  } catch {
    return null;
  }
};

export const readFreshBootstrapSnapshot = (userId?: string | null): BootstrapSnapshot | null => {
  const snapshot = readBootstrapSnapshot(userId);
  if (snapshot === null) return null;
  return Date.now() - snapshot.savedAt <= bootstrapSnapshotTtlMs ? snapshot : null;
};

export const writeBootstrapProjectOptionCaches = (
  projects: ProjectDto[],
  userId?: string | null,
): void => {
  const nextMap: Record<string, ProjectOption[]> = {
    [getProjectCacheKey(null)]: projects
      .filter((project) => project.organisationId === null)
      .map(toProjectOption),
  };
  const organisationIds = Array.from(
    new Set(
      projects
        .map((project) => project.organisationId)
        .filter((organisationId): organisationId is string => organisationId !== null),
    ),
  );
  organisationIds.forEach((organisationId) => {
    nextMap[getProjectCacheKey(organisationId)] = projects
      .filter((project) => project.organisationId === organisationId)
      .map(toProjectOption);
  });
  writeProjectOptionsCacheMap(nextMap, userId);
};

export const writeProjectStateCaches = ({
  projects,
  organisations,
  userId,
}: {
  projects: ProjectDto[];
  organisations: OrganisationOption[];
  userId?: string | null;
}): void => {
  writeBootstrapProjectOptionCaches(projects, userId);
  writeBootstrapSnapshot(
    {
      savedAt: Date.now(),
      organisations,
      projects,
    },
    userId,
  );
};

export const resolveBootstrapUserId = (session: AuthSession | null): string | null =>
  session?.userId ?? getStoredSession()?.userId ?? null;
